package one.healthium.log_food.data

import co.touchlab.kermit.Logger
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.functions.FirebaseFunctions
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import one.healthium.db.dao.FoodDao
import one.healthium.locale.LocaleProvider
import one.healthium.log_food.data.api.FoodScanApiClient
import one.healthium.log_food.data.image.ImageReader
import one.healthium.log_food.data.image.compressImage
import one.healthium.log_food.domain.FoodModel
import one.healthium.log_food.domain.FoodRepository

class AppFoodRepository(
    private val foodMapper: FoodMapper,
    private val foodDao: FoodDao,
    private val functions: FirebaseFunctions,
    private val localeProvider: LocaleProvider,
    private val foodScanApiClient: FoodScanApiClient,
    private val auth: FirebaseAuth,
    private val imageReader: ImageReader,
) : FoodRepository {

    override suspend fun suggestions(keyword: String): List<FoodModel> {
        return foodDao.getFoods(keyword)
            .map { foodMapper.entityToModel(it) }
            .sortedWith(compareBy { it.product.indexOf(keyword, ignoreCase = true) })
    }

    override suspend fun searchFoodByName(query: String): List<FoodModel> {
        Logger.d("searchFoodByName: $query")
        val response = functions
            .httpsCallable("getFoodNutritionDataV2")
            .invoke(Request(name = query, locale = localeProvider.getLocale()))
            .data(ListSerializer(FoodDto.serializer()))

        Logger.d("foods: $response")

        return response.map(foodMapper::dtoToModel)
    }

    override suspend fun searchFoodByCode(barcode: String): List<FoodModel> {
        Logger.d("searchFoodByCode: $barcode")
        val response = functions
            .httpsCallable("getFoodNutritionDataV2")
            .invoke(Request(code = barcode, locale = localeProvider.getLocale()))
            .data(ListSerializer(FoodDto.serializer()))

        Logger.d("foods: $response")

        return response.map(foodMapper::dtoToModel)
    }

    override suspend fun scanFoodByPhoto(imageUri: String): List<FoodModel> {
        val rawBytes = imageReader.readBytes(imageUri)
        return scanFoodByPhoto(rawBytes)
    }

    override suspend fun scanFoodByPhoto(imageBytes: ByteArray): List<FoodModel> {
        val compressed = compressImage(imageBytes)
        val idToken = auth.currentUser?.getIdToken(false) ?: throw IllegalStateException("User not authenticated")
        val locale = localeProvider.getLocale()
        val foods = foodScanApiClient.scanFood(compressed, locale, idToken)
        return foods.map(foodMapper::dtoToModel)
    }
}

@Serializable
private data class Request(
    @SerialName("food") val name: String? = null,
    @SerialName("code") val code: String? = null,
    @SerialName("locale") val locale: String,
)